import { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { push } from 'redux-first-history';
import { pushToDataLayer } from '../../common/analytics';
import { isEoe, isPunchoutPath, paths } from '../../common/constants/pathVariables';
import { CommonErrorType, DialogType, SubscriptionCategory, SwitchAccountJourneyPoint } from '../../common/enums';
import { callUiApi, postIdentifyStrongly } from '../../common/fetch';
import { useSingletonCallback } from '../../common/hooks/useSingletonCallback';
import { t } from '../../common/i18n';
import { convertToCommonErrors } from '../../common/utils/errorUtils';
import {
  getErrorsFromEmployeeSubscriptionPairingScene,
  getErrorsFromValidateEmployeeSubscriptionResponse,
} from '../../components/EoeSubscriptionPairingScene/eoeSubscriptionPairingSceneUtils';
import { getErrorsFromOneTimePasswordComponent } from '../../components/OneTimePasswordForm/oneTimePasswordFormUtils';
import { getErrorsFromUpdateUserDetails } from '../../components/UserDetails/userDetailsUtils';
import { ConflictedAuthenticationResult } from '../../generated/api/conflictedAuthenticationResult';
import { IdentityProvider } from '../../generated/api/identityProvider';
import { AuthenticatedUserRole } from '../../generated/api/models';
import {
  getMyselfUserPrivateMethod,
  logInAndAddMissingMobileNumberPublicMethod,
  logInAndSavePrimaryAccountPublicMethod,
  logInPublicMethod,
  logOutPrivateMethod,
  logoutPublicMethod,
  prepareLoginPublicMethod,
  processElisaIdV2LoginPublicMethod,
  requestEmployeeOtpForPairingPrivateMethod,
  resetPasswordPublicMethod,
  resolveMissingPrimaryAccountPublicMethod,
  resolveUserNameConflictPrivateMethod,
  sendOtpToUserPublicMethod,
  switchAccountPrivateMethod,
  updateMyselfContactPrivateMethod,
  validateEmployeeOtpAndPairPrivateMethod,
  validateOtpPublicMethod,
  validatePublicMethod,
  verifyEmailPublicMethod,
} from '../../generated/api/uiApiMethods';
import { ValidationConflict } from '../../generated/api/validationConflict';
import {
  clearCartItems,
  getEmployeeSubscriptions,
  loadVirtualCatalogs,
  moveCheckoutToNextStep,
  notifyShoppingCartChange,
  stopPollForMyselfChanges,
  validateAuxiliaryEsimOrderFailed,
} from '../../selfservice/actions';
import {
  clearEmployeeSubs,
  clearState,
  loadAuxiliaryEsimSubscription,
  subscribeAuthAction,
  unsubscribeAuthAction,
  updatePendingSubscriptionActions,
} from '../../selfservice/actions/authSideEffectActions';
import {
  clearElisaIdSessionData,
  getActiveAccountMasterId,
  getAuthRedirectUrl,
  removeActiveAccountMasterId,
  removePrimaryMdmId,
  removeSwitchToAccountName,
  setActiveAccountMasterId,
  setSwitchToAccountName,
} from '../../selfservice/common/localStorageUtils';
import { clearOnboardingId, getOnboardingId } from '../../selfservice/epics/epicUtils';
import { logoutElisaIdV2session } from '../common/util/elisaIdLogin';
import { SiteContext } from './SiteContext';
import {
  elisaIdEoeLoginFail,
  elisaIdEoeLoginSuccess,
  filterErrors,
  getSignatoryRightsConflictType,
  handleAuthFailedError,
  handleConflictedError,
  handleRedirectResponse,
  isAdditionalInfoRequiredError,
  isIndividualNotStronglyIdentifiedError,
  isInvitationAcceptanceRequired,
  processConflictedErrors,
  reduceAuthenticationResult,
  showAuthFailedDialog,
  ssoNoeLoginFail,
  ssoNoeLoginSuccess,
  toCommonErrors,
  userAuthValidate,
} from './authUtils';
import { closeDialog, openDialog, setDialogSubmitting } from './dialogStore.js';
import { addNotification } from './notificationStore.js';
import { clearSessionData, refreshCompanyInfoSession } from './sessionDataStore';
import type { User, UserSetter } from './authState';
import type { CommonError } from '../../common/types/errors';
import type { CompanyInfoResponse } from '../../generated/api/companyInfoResponse';
import type { ContactPerson } from '../../generated/api/contactPerson';
import type { LoginResponse } from '../../generated/api/loginResponse';
import type { MfaInfo } from '../../generated/api/mfaInfo';
import type {
  AuthenticationResult,
  SubscriptionPairingRequest,
  ValidateSubscriptionPairingResponse,
  ValidationConflictResponse,
} from '../../generated/api/models';
import type { ResetPasswordRequest } from '../../generated/api/resetPasswordRequest';
import type { ResolveUserNameConflictRequest } from '../../generated/api/resolveUserNameConflictRequest';
import type { SendOtpToUserRequest } from '../../generated/api/sendOtpToUserRequest';
import type { ValidateOtpRequest } from '../../generated/api/validateOtpRequest';
import type { ValidateSubscriptionPairingPayload } from '../../selfservice/actions';

// TODO: Check how to combine AuthProvider errors and Redux errors
// TODO: Move API calls to fetch.ts
// TODO: Move Elisa ID V2 API calls from elisaIdLogin.ts here
// TODO: Check if the difference between redux and react state updates cause any issues ->
//  redux state updates happens independent of epics, but with react, state updates only after API response
// TODO: Validate is called multiple times from Checkout.tsx currently, as authValidate depends on activeAccountMasterId, which changes after the first call
//  this causes authValidate reference to change, which causes the Checkout.tsx useEffect to trigger again

export interface AuthServiceProps {
  prepareElisaIdLogin: (options?: {
    callPath?: string;
    invitationId?: string;
    noContextClient?: boolean;
    isEmployee?: boolean;
    register?: boolean;
    includeAdmins?: boolean;
    migrate?: boolean;
    changeContext?: boolean;
  }) => void;
  processElisaIdLogin: (authCode: string) => void;
  logout: (redirectTo?: string, skipApiCall?: boolean, isPreAuthLogout?: boolean) => void;
  addMissingMobileNumberAndLogIn: (userName: string, password: string, mobileNumber: string) => void;
  selectPrimaryAccountAndLogIn: (userName: string, password: string, primaryAccountMasterId: string) => void;
  resolveMissingPrimaryAccount: (primaryAccountMasterId: string) => void;
  validateAuth: () => void;
  login: (username: string, password: string) => void;
  verifyEmail: (linkToken: string) => void;
  updateForgotPasswordStepNumber: (stepNumber: number) => void;
  sendOtpToUser: (data: SendOtpToUserRequest) => void;
  validateOtp: (data: ValidateOtpRequest) => void;
  resetPassword: (data: ResetPasswordRequest) => void;
  selectUsername: (request: ResolveUserNameConflictRequest) => void;
  getUser: () => void;
  updateUserDetails: (
    userDetails: ContactPerson,
    customerType?: CompanyInfoResponse.CustomerTypeEnum,
    validationErrors?: CommonError[]
  ) => void;
  mfaRequired: (mfaInfo: MfaInfo) => void;
  setActiveAccount: (
    activeAccountMasterId: string,
    reloadWindow?: boolean,
    redirectUrl?: string,
    switchToAccountName?: string
  ) => void;
  switchAccount: () => void;
  identifyStrongly: (idServiceCode: string) => Promise<void>;
  processSubscriptionPairing: (request: SubscriptionPairingRequest, validationErrors?: CommonError[]) => void;
  validateSubscriptionPairing: (request: ValidateSubscriptionPairingPayload, validationErrors?: CommonError[]) => void;
}

export const useAuthService = (user: User, setUser: UserSetter): AuthServiceProps => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { siteLanguage } = useContext(SiteContext);
  const navigate = useNavigate();

  // Replaces SET_ACTIVE_ACCOUNT
  const setActiveAccount = useSingletonCallback(
    (activeAccountMasterId: string, reloadWindow = false, redirectUrl?: string, switchToAccountName?: string) => {
      setUser(prevUser => {
        if (prevUser.authenticated) {
          prevUser.authenticated.activeAccountMasterId = activeAccountMasterId;
        }
      });
      setActiveAccountMasterId(activeAccountMasterId);
      void refreshCompanyInfoSession(
        activeAccountMasterId,
        isEoe(window.location.pathname),
        isPunchoutPath(window.location.pathname)
      );
      if (switchToAccountName) {
        setSwitchToAccountName(switchToAccountName);
      }
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else if (reloadWindow) {
        // Reload the window, needed for multibiz users to refresh all the company data when changing account.
        window.location.reload();
      } else {
        removeSwitchToAccountName();
      }
    },
    [setUser]
  );

  // Replaces MFA_REQUIRED
  const mfaRequired = useSingletonCallback(
    (mfaInfo: MfaInfo) => {
      setUser(prevUser => {
        prevUser.anonymous.ssoSessionValid = false;
        prevUser.anonymous.mfaInfo = mfaInfo;
        if (prevUser.authenticated) {
          prevUser.authenticated.isFetching = false;
        }
      });
    },
    [setUser]
  );

  const prepareElisaIdLogin = useSingletonCallback(
    async (options?: {
      callPath?: string;
      invitationId?: string;
      noContextClient?: boolean;
      isEmployee?: boolean;
      register?: boolean;
      includeAdmins?: boolean;
      migrate?: boolean;
      changeContext?: boolean;
    }) => {
      let callPath = options?.callPath ?? decodeURIComponent(globalThis.location.pathname);

      if (options?.invitationId) {
        callPath = options.isEmployee ? '/työntekijä' : '/omaelisa';
      }

      const prepareLoginUrl = prepareLoginPublicMethod({
        callPath,
        locale: siteLanguage.substring(0, 2),
        noPrompt: false,
        invitationId: options?.invitationId,
        noContextClient: options?.noContextClient,
        register: options?.register,
        includeAdmins: options?.includeAdmins,
        migrate: options?.migrate,
        changeContext: options?.changeContext,
      });
      globalThis.location.href = prepareLoginUrl.path;
    },
    [siteLanguage]
  );

  // Replaces PROCESS_ELISAID_LOGIN, PROCESS_ELISAID_LOGIN_FULFILLED,PROCESS_ELISAID_LOGIN_FAILED
  const processElisaIdLogin = useSingletonCallback(
    async (authCode: string) => {
      setUser(prevUser => {
        prevUser.anonymous.elisaIdV2LoginProcessing = true;
      });
      removeActiveAccountMasterId();
      const redirectUrl = getAuthRedirectUrl();
      if (!redirectUrl) {
        throw new Error('Redirect uri must be available in session storage for making process call');
      }
      try {
        const response: LoginResponse = await (
          await callUiApi(
            processElisaIdV2LoginPublicMethod({
              authCode: authCode,
              redirectUrl: redirectUrl,
              onboardingId: getOnboardingId() || undefined,
            })
          )
        ).json();
        setUser(prevUser => {
          prevUser.anonymous.ssoSessionValid = true;
          prevUser.anonymous.elisaIdV2LoginProcessing = false;
          reduceAuthenticationResult(prevUser, response.result);
        });
        if (response.result.account?.businessId) {
          if (getOnboardingId()) {
            clearOnboardingId();
          }
          clearElisaIdSessionData();
        }
        dispatch(loadAuxiliaryEsimSubscription());
        if (isEoe(window.location.pathname)) {
          dispatch(loadVirtualCatalogs());
        }
        dispatch(updatePendingSubscriptionActions(response.result.pendingSubscriptionActions));
        pushToDataLayer(elisaIdEoeLoginSuccess(response.result));
      } catch (error) {
        const commonErrors = await toCommonErrors(error);
        setUser(prevUser => {
          // TODO: Simplify error handling. Current flow: ajax error -> common error -> process conflicted errors -> filter errors
          prevUser.anonymous.errors = filterErrors(processConflictedErrors(commonErrors));
          prevUser.anonymous.elisaIdV2LoginProcessing = false;
        });
        if (
          commonErrors?.some(
            commonError =>
              commonError.type === CommonErrorType.BAD_REQUEST ||
              commonError.type === CommonErrorType.NOT_FOUND ||
              commonError.category === ConflictedAuthenticationResult.TypeEnum.ONBOARDING_LINK_OLD_OR_INVALID ||
              commonError.category === ConflictedAuthenticationResult.TypeEnum.ONBOARDING_LINK_USED_OR_EXPIRED
          )
        ) {
          clearOnboardingId();
          clearElisaIdSessionData();
        }
        const conflictDialogParams = handleConflictedError(commonErrors, SwitchAccountJourneyPoint.ONBOARDING);
        if (conflictDialogParams) {
          openDialog(conflictDialogParams);
        }
        dispatch(validateAuxiliaryEsimOrderFailed('Login failed', error.status));
        pushToDataLayer(elisaIdEoeLoginFail(commonErrors));
      }
    },
    [dispatch, setUser]
  );

  // Replaces LOG_OUT, LOGOUT_FULFILLED, LOGOUT_FAILED
  const logout = useSingletonCallback(
    async (redirectTo: string = '', skipApiCall: boolean = false, isPreAuthLogout: boolean = false) => {
      removePrimaryMdmId();
      clearSessionData();
      if (user.authenticated?.identityProvider === IdentityProvider.ELISA_ID_V2) {
        await logoutElisaIdV2session(
          redirectTo ? window.location.origin + redirectTo : window.location.origin + decodeURIComponent(pathname)
        );
        dispatch(clearState());
        return;
      }
      if (user.authenticated?.identityProvider === IdentityProvider.ELISA_ID || isPreAuthLogout) {
        window.location.href = logoutPublicMethod().path;
        return;
      }
      setUser(prevUser => {
        prevUser.anonymous.ssoSessionValid = false;
      });
      if (user.authenticated) {
        try {
          if (!skipApiCall) {
            await callUiApi(logOutPrivateMethod());
          }
          removeActiveAccountMasterId();
          setUser(prevUser => {
            prevUser.authenticated = undefined;
          });
          dispatch(clearState());
          dispatch(stopPollForMyselfChanges());
          if (redirectTo) {
            window.location.href = redirectTo;
          }
        } catch (error) {
          setUser(prevUser => {
            prevUser.authenticated = undefined;
          });
          dispatch(clearState());
        }
      } else {
        setUser(prevUser => {
          prevUser.authenticated = undefined;
        });
        dispatch(clearState());
        if (redirectTo) {
          window.location.href = redirectTo;
        }
      }
    },
    [dispatch, pathname, setUser, user.authenticated]
  );

  const onValidateAuthFailed = useSingletonCallback(
    (commonErrors: CommonError[]) => {
      setUser(prevUser => {
        prevUser.anonymous.ssoSessionValid = false;
        if (commonErrors[0].source?.statusCode === 409) {
          prevUser.authenticated!.isFetching = false;
        } else {
          prevUser.authenticated = undefined;
        }
      });
      removeActiveAccountMasterId();
      handleAuthFailedError(commonErrors);
    },
    [setUser]
  );

  // Replaces AUTH_VALIDATE, AUTH_VALIDATE_FULFILLED, AUTH_VALIDATE_FAILED
  const validateAuth = useSingletonCallback(async () => {
    setUser(prevUser => {
      // TODO: Fix below workaround (copied from redux). isFetching should be moved out of authenticated, or
      //  authenticated should be made required with empty companyName and firstName
      // @ts-ignore
      prevUser.authenticated = {
        ...prevUser.authenticated,
        isFetching: true,
      };
      prevUser.anonymous.isValidating = true;
    });
    try {
      const response = await callUiApi(validatePublicMethod());
      const result = await response.json();
      if (response.status === 204) {
        handleRedirectResponse(response);
        onValidateAuthFailed(convertToCommonErrors('Redirected', 204));
      } else if (response.status === 202) {
        mfaRequired(result);
      } else if (result.valid && result.result) {
        const authResult = result.result as AuthenticationResult;
        setUser(prevUser => {
          prevUser.anonymous.ssoSessionValid = true;
          prevUser.anonymous.mfaInfo = undefined;
          reduceAuthenticationResult(prevUser, authResult);
        });
        pushToDataLayer(userAuthValidate(authResult));
        const storedActiveAccount = getActiveAccountMasterId();
        const currentActiveAccount = user.authenticated?.activeAccountMasterId;
        if (!storedActiveAccount && authResult.account?.accountMasterId) {
          setActiveAccount(authResult.account.accountMasterId);
        } else if (storedActiveAccount && storedActiveAccount !== currentActiveAccount) {
          setActiveAccount(storedActiveAccount);
        }
        if (
          isEoe(window.location.pathname) &&
          ![IdentityProvider.ELISA_ID_V2, IdentityProvider.ELISA_ID].includes(authResult.user.identityProvider)
          // TODO: also check for strong authenticated user once we have that info in the auth result, as we only want to show the dialog for non-strongly authenticated users
        ) {
          openDialog({ type: DialogType.ADMIN_FORBIDDEN });
        }
        if (isEoe(window.location.pathname)) {
          dispatch(loadVirtualCatalogs());
        }
        dispatch(updatePendingSubscriptionActions(authResult.pendingSubscriptionActions));
      } else {
        onValidateAuthFailed(convertToCommonErrors('SSO token or JWT not valid', 200));
      }
      setUser(prevUser => {
        prevUser.anonymous.isValidating = false;
      });
    } catch (error) {
      const commonErrors = await toCommonErrors(error);
      const signatoryRightsConflictType = getSignatoryRightsConflictType(commonErrors);
      if (isIndividualNotStronglyIdentifiedError(commonErrors)) {
        removeActiveAccountMasterId();
        setUser(prevUser => {
          prevUser.anonymous.isValidating = false;
          prevUser.anonymous.ssoSessionValid = false;
          prevUser.authenticated = undefined;
        });
        navigate(paths.ERROR);
      } else if (signatoryRightsConflictType) {
        removeActiveAccountMasterId();
        navigate(paths.SELECT_SIGNATORY_RIGHTS, {
          state: {
            individualDetails: (commonErrors[0]?.source?.error as unknown as ValidationConflictResponse)
              ?.individualDetails,
            contactSignatoryRights: (commonErrors[0]?.source?.error as unknown as ValidationConflictResponse)
              ?.contactSignatoryRights,
            showLinkedCompanies: signatoryRightsConflictType === ValidationConflict.CHOOSE_ADMIN_OR_SIGNATORY_RIGHT,
            redirectTo: pathname || undefined,
          },
        });
      } else if (isAdditionalInfoRequiredError(commonErrors)) {
        navigate(paths.EMPLOYEE_SELF_REGISTRATION, {
          state: {
            individualDetails: (commonErrors[0]?.source?.error as unknown as ValidationConflictResponse)
              ?.individualDetails,
          },
        });
      } else if (isInvitationAcceptanceRequired(commonErrors)) {
        navigate(paths.EMPLOYEE_INVITATION_ACCEPTANCE, {
          state: {
            individualDetails: (commonErrors[0]?.source?.error as unknown as ValidationConflictResponse)
              ?.individualDetails,
            invitedEmail: (commonErrors[0]?.source?.error as unknown as ValidationConflictResponse)?.invitedEmail,
          },
        });
      } else {
        onValidateAuthFailed(commonErrors);
        setUser(prevUser => {
          prevUser.anonymous.isValidating = false;
        });
      }
    }
  }, [
    dispatch,
    mfaRequired,
    onValidateAuthFailed,
    setActiveAccount,
    setUser,
    user.authenticated?.activeAccountMasterId,
    navigate,
    pathname,
  ]);

  const onLoginSuccess = useSingletonCallback(
    async (response: Response) => {
      const result: LoginResponse = await response.json();
      setUser(prevUser => {
        prevUser.anonymous.ssoSessionValid = true;
        prevUser.anonymous.forgotPasswordStepNumber = 0;
        prevUser.anonymous.mfaInfo = undefined;
        reduceAuthenticationResult(prevUser, result.result);
      });
      dispatch(updatePendingSubscriptionActions(result.result.pendingSubscriptionActions));
      closeDialog();
      if (result.result.account?.accountMasterId) {
        setActiveAccount(result.result.account.accountMasterId);
      }
      if (result.result.user?.userRole === AuthenticatedUserRole.EMPLOYEE) {
        dispatch(clearCartItems());
        dispatch(push('/'));
      } else {
        dispatch(notifyShoppingCartChange());
      }
      pushToDataLayer(ssoNoeLoginSuccess(result.result));
    },
    [dispatch, setActiveAccount, setUser]
  );

  const onLoginFailed = useSingletonCallback(
    (commonErrors: CommonError[], userName?: string, password?: string) => {
      setUser(prevUser => {
        prevUser.anonymous.errors = commonErrors;
        prevUser.anonymous.forgotPasswordStepNumber = 0;
      });
      const conflictDialogParams = handleConflictedError(commonErrors);
      if (conflictDialogParams) {
        openDialog(conflictDialogParams);
        return;
      }
      handleAuthFailedError(commonErrors, userName, password);
      pushToDataLayer(ssoNoeLoginFail(commonErrors));
    },
    [setUser]
  );

  // Replaces LOG_IN, LOG_IN_FULFILLED, LOG_IN_FAILED
  const login = useSingletonCallback(
    async (username: string, password: string) => {
      setDialogSubmitting(true);
      try {
        const payload = {
          password: password,
          userName: username,
        };
        removeActiveAccountMasterId();
        const response = await callUiApi(logInPublicMethod(), {
          body: JSON.stringify(payload),
        });
        if (response.status === 204) {
          handleRedirectResponse(response);
          onLoginFailed(convertToCommonErrors('Redirected', 204));
        } else if (response.status === 202) {
          const mfaInfo = await response.json();
          mfaRequired(mfaInfo);
        } else {
          onLoginSuccess(response);
        }
      } catch (error) {
        const commonErrors = await toCommonErrors(error);
        onLoginFailed(commonErrors, username, password);
      }
    },
    [mfaRequired, onLoginFailed, onLoginSuccess]
  );

  // Replaces ADD_MISSING_MOBILE_NUMBER_AND_LOG_IN, ADD_MISSING_MOBILE_NUMBER_AND_LOG_IN_FAILED
  const addMissingMobileNumberAndLogIn = useSingletonCallback(
    async (userName: string, password: string, mobileNumber: string) => {
      setDialogSubmitting(true);
      try {
        const payload = {
          password: password,
          mobileNumber: mobileNumber,
          username: userName,
        };
        removeActiveAccountMasterId();
        const response = await callUiApi(logInAndAddMissingMobileNumberPublicMethod(), {
          body: JSON.stringify(payload),
        });
        if (response.status === 202) {
          const mfaInfo = await response.json();
          mfaRequired(mfaInfo);
        } else {
          await onLoginSuccess(response);
        }
      } catch (error) {
        const commonErrors = await toCommonErrors(error);
        handleAuthFailedError(commonErrors, userName, password);
      }
    },
    [mfaRequired, onLoginSuccess]
  );

  // Replaces SELECT_PRIMARY_ACCOUNT_AND_LOG_IN, SELECT_PRIMARY_ACCOUNT_AND_LOG_IN_FAILED
  const selectPrimaryAccountAndLogIn = useSingletonCallback(
    async (userName: string, password: string, primaryAccountMasterId: string) => {
      setDialogSubmitting(true);
      try {
        const payload = {
          password: password,
          primaryAccountMasterId: primaryAccountMasterId,
          username: userName,
        };
        removeActiveAccountMasterId();
        const response = await callUiApi(logInAndSavePrimaryAccountPublicMethod(), {
          body: JSON.stringify(payload),
        });
        if (response.status === 202) {
          const mfaInfo = await response.json();
          mfaRequired(mfaInfo);
        } else {
          await onLoginSuccess(response);
        }
      } catch (error) {
        const commonErrors = await toCommonErrors(error);
        handleAuthFailedError(commonErrors, userName, password);
      }
    },
    [mfaRequired, onLoginSuccess]
  );

  // Replaces RESOLVE_MISSING_PRIMARY_ACCOUNT, RESOLVE_MISSING_PRIMARY_ACCOUNT_FULFILLED, RESOLVE_MISSING_PRIMARY_ACCOUNT_FAILED
  const resolveMissingPrimaryAccount = useSingletonCallback(
    async (primaryAccountMasterId: string) => {
      try {
        const payload = {
          primaryAccountMasterId: primaryAccountMasterId,
        };
        await callUiApi(resolveMissingPrimaryAccountPublicMethod(), {
          body: JSON.stringify(payload),
        });
        await validateAuth();
        closeDialog();
      } catch (error) {
        showAuthFailedDialog();
      }
    },
    [validateAuth]
  );

  const updateAuthResult = useSingletonCallback(
    (authenticationResult: AuthenticationResult) => {
      setUser(prevUser => {
        reduceAuthenticationResult(prevUser, authenticationResult);
      });
    },
    [setUser]
  );

  // Replaces VERIFY_EMAIL, VERIFY_EMAIL_FULFILLED, VERIFY_EMAIL_FAILED
  const verifyEmail = useSingletonCallback(
    async (linkToken: string) => {
      try {
        await callUiApi(verifyEmailPublicMethod(), {
          body: JSON.stringify({
            linkToken,
          }),
        });
        setUser(prevUser => {
          // TODO: Fix below workaround (copied from redux). verifyEmail is invoked before authValidate,
          //  so authenticated state is not initialized yet
          // @ts-ignore
          prevUser.authenticated = {
            ...prevUser.authenticated,
            emailVerified: true,
          };
        });
      } catch (error) {
        const commonErrors = await toCommonErrors(error);
        setUser(prevUser => {
          prevUser.anonymous.errors = processConflictedErrors(commonErrors);
        });
      }
    },
    [setUser]
  );

  // Replaces FORGOT_PASSWORD_STEP_NUMBER
  const updateForgotPasswordStepNumber = useSingletonCallback(
    (stepNumber: number) => {
      // TODO: This should be handled within component's local state
      setUser(prevUser => {
        prevUser.anonymous.forgotPasswordStepNumber = stepNumber;
      });
    },
    [setUser]
  );

  // Replaces SEND_OTP_TO_USER, SEND_OTP_TO_USER_FULFILLED, SEND_OTP_TO_USER_FAILED
  const sendOtpToUser = useSingletonCallback(
    async (data: SendOtpToUserRequest) => {
      setUser(prevUser => {
        prevUser.anonymous.forgotPasswordErrored = false;
        prevUser.anonymous.forgotPasswordInProgress = true;
      });
      try {
        await callUiApi(sendOtpToUserPublicMethod(), {
          body: JSON.stringify(data),
        });
        setUser(prevUser => {
          prevUser.anonymous.forgotPasswordStepNumber = (prevUser.anonymous.forgotPasswordStepNumber || 0) + 1;
          prevUser.anonymous.forgotPasswordErrored = false;
          prevUser.anonymous.forgotPasswordInProgress = false;
        });
      } catch (error) {
        setUser(prevUser => {
          // TODO: Validate the state updates. Its currently same in case of success and failure
          prevUser.anonymous.forgotPasswordStepNumber = (prevUser.anonymous.forgotPasswordStepNumber || 0) + 1;
          prevUser.anonymous.forgotPasswordErrored = false;
          prevUser.anonymous.forgotPasswordInProgress = false;
        });
      }
    },
    [setUser]
  );

  // Replaces VALIDATE_OTP, VALIDATE_OTP_FULFILLED, VALIDATE_OTP_FAILED
  const validateOtp = useSingletonCallback(
    async (data: ValidateOtpRequest) => {
      setUser(prevUser => {
        prevUser.anonymous.forgotPasswordErrored = false;
        prevUser.anonymous.forgotPasswordInProgress = true;
      });
      try {
        await callUiApi(validateOtpPublicMethod(), {
          body: JSON.stringify(data),
        });
        setUser(prevUser => {
          prevUser.anonymous.forgotPasswordStepNumber = (prevUser.anonymous.forgotPasswordStepNumber || 0) + 1;
          prevUser.anonymous.forgotPasswordErrored = false;
          prevUser.anonymous.forgotPasswordInProgress = false;
        });
      } catch (error) {
        setUser(prevUser => {
          prevUser.anonymous.forgotPasswordErrored = true;
          prevUser.anonymous.forgotPasswordInProgress = false;
        });
      }
    },
    [setUser]
  );

  // Replaces RESET_PASSWORD, RESET_PASSWORD_FULFILLED, RESET_PASSWORD_FAILED
  const resetPassword = useSingletonCallback(
    async (data: ResetPasswordRequest) => {
      setUser(prevUser => {
        prevUser.anonymous.forgotPasswordErrored = false;
        prevUser.anonymous.forgotPasswordInProgress = true;
      });

      try {
        await callUiApi(resetPasswordPublicMethod(), {
          body: JSON.stringify(data),
        });
        setUser(prevUser => {
          prevUser.anonymous.forgotPasswordStepNumber = (prevUser.anonymous.forgotPasswordStepNumber || 0) + 1;
          prevUser.anonymous.forgotPasswordErrored = false;
          prevUser.anonymous.forgotPasswordInProgress = false;
        });
      } catch (error) {
        setUser(prevUser => {
          prevUser.anonymous.forgotPasswordErrored = true;
          prevUser.anonymous.forgotPasswordInProgress = false;
        });
      }
    },
    [setUser]
  );

  // Replaces SELECT_USERNAME, SELECT_USERNAME_FULFILLED, SELECT_USERNAME_FAILED
  const selectUsername = useSingletonCallback(async (request: ResolveUserNameConflictRequest) => {
    setDialogSubmitting(true);
    try {
      await callUiApi(resolveUserNameConflictPrivateMethod(), {
        body: JSON.stringify(request),
      });
      closeDialog();
    } catch (error) {
      const commonErrors = await toCommonErrors(error);
      handleAuthFailedError(commonErrors);
    }
  }, []);

  // Replaces GET_USER, GET_USER_FULFILLED, GET_USER_FAILED
  const getUser = useSingletonCallback(async () => {
    try {
      const response = await (await callUiApi(getMyselfUserPrivateMethod())).json();
      setUser(prevUser => {
        if (prevUser.authenticated) {
          prevUser.authenticated.firstName = prevUser.authenticated.firstName || response.firstName || '';
          prevUser.authenticated.identityProviderEmail = response.email || null;
          prevUser.authenticated.identityProviderPhone = response.mobile || null;
          prevUser.authenticated.lastName = prevUser.authenticated.lastName || response.lastName;
        }
      });
    } catch (error) {
      const commonErrors = await toCommonErrors(error);
      setUser(prevUser => {
        if (prevUser.authenticated) {
          prevUser.authenticated.errors = commonErrors;
        }
      });
    }
  }, [setUser]);

  // Replaces UPDATE_USER_DETAILS, UPDATE_USER_DETAILS_FULFILLED, UPDATE_USER_DETAILS_FAILED
  const updateUserDetails = useSingletonCallback(
    async (
      userDetails: ContactPerson,
      customerType?: CompanyInfoResponse.CustomerTypeEnum,
      validationErrors?: CommonError[]
    ) => {
      // TODO: This should be handled within component's local state
      const errors = getErrorsFromUpdateUserDetails(userDetails, customerType, validationErrors);
      if (errors) {
        setUser(prevUser => {
          if (prevUser.authenticated) {
            prevUser.authenticated.errors = errors;
          }
        });
        dispatch(moveCheckoutToNextStep(errors));
        return;
      }
      dispatch(moveCheckoutToNextStep());
      const hasChanges =
        user.authenticated?.analyticsConsent !== userDetails.analyticsConsent ||
        user.authenticated?.preferredLanguage !== userDetails.preferredLanguage ||
        user.authenticated?.email !== userDetails.email ||
        user.authenticated?.firstName !== userDetails.firstName ||
        user.authenticated?.lastName !== userDetails.lastName ||
        user.authenticated?.mobile !== userDetails.phoneNumber ||
        user.authenticated?.costCenter !== userDetails.costCenter ||
        user.authenticated?.employeeNumber !== userDetails.employeeNumber;
      if (!hasChanges) {
        setUser(prevUser => {
          if (prevUser.authenticated) {
            prevUser.authenticated.errors = undefined;
            prevUser.authenticated.saving = false;
          }
        });
        return;
      }
      setUser(prevUser => {
        if (prevUser.authenticated) {
          prevUser.authenticated.errors = undefined;
          prevUser.authenticated.saving = true;
        }
      });
      try {
        const onboardingId = getOnboardingId() || undefined;
        const isCookieConsentUpdated =
          !!(user?.authenticated && userDetails.analyticsConsent) &&
          user.authenticated.analyticsConsent !== userDetails.analyticsConsent;
        const response = await callUiApi(
          updateMyselfContactPrivateMethod({
            onboardingId,
            forceUpsert: isCookieConsentUpdated,
          }),
          {
            body: JSON.stringify(userDetails),
          }
        );
        clearOnboardingId();
        const isUserOnboarded = response.status === 201;
        setUser(prevUser => {
          if (prevUser.authenticated) {
            prevUser.authenticated.analyticsConsent =
              userDetails.analyticsConsent || prevUser.authenticated.analyticsConsent;
            prevUser.authenticated.emailVerified =
              isUserOnboarded ||
              (prevUser.authenticated.emailVerified &&
                (prevUser.authenticated.email === userDetails.email || !!prevUser.authenticated.businessId));
            prevUser.authenticated.email = userDetails.email;
            prevUser.authenticated.employeeNumber = userDetails.employeeNumber;
            prevUser.authenticated.firstName = userDetails.firstName;
            prevUser.authenticated.costCenter = userDetails.costCenter;
            prevUser.authenticated.lastName = userDetails.lastName;
            prevUser.authenticated.mobile = userDetails.phoneNumber;
            prevUser.authenticated.preferredLanguage =
              userDetails.preferredLanguage || prevUser.authenticated.preferredLanguage;
            prevUser.authenticated.saving = false;
            prevUser.authenticated.errors = undefined;
          }
        });
        if (!isCookieConsentUpdated) {
          addNotification(t.PS8D('Data update was successful.'));
        }
        closeDialog();
        if (onboardingId && !user.authenticated?.companyName) {
          await validateAuth();
        }
      } catch (error) {
        const commonErrors = await toCommonErrors(error);
        setUser(prevUser => {
          if (prevUser.authenticated) {
            prevUser.authenticated.errors = filterErrors(processConflictedErrors(commonErrors));
            prevUser.authenticated.saving = false;
          }
        });
        const conflictDialogParams = handleConflictedError(commonErrors);
        if (conflictDialogParams) {
          openDialog(conflictDialogParams);
        }
      }
    },
    [dispatch, setUser, validateAuth, user.authenticated]
  );

  // Replaces SWITCH_ACCOUNT, SWITCH_ACCOUNT_FULFILLED, SWITCH_ACCOUNT_FAILED
  const switchAccount = useSingletonCallback(async () => {
    setDialogSubmitting(true);
    try {
      await callUiApi(switchAccountPrivateMethod());
      dispatch(clearState());
      await validateAuth();
      if (decodeURI(window.location.pathname) !== paths.EMPLOYEE_HOME) {
        dispatch(push(paths.EMPLOYEE_HOME));
      }
    } catch (error) {
      showAuthFailedDialog();
    }
  }, [dispatch, validateAuth]);

  const identifyStrongly = useSingletonCallback(
    async (idServiceCode: string) => {
      await postIdentifyStrongly(idServiceCode);
      setUser(prevUser => {
        // TODO: Fix below workaround (copied from redux). identifyStrongly is invoked before authValidate,
        //  so authenticated state is not initialized yet
        // @ts-ignore
        prevUser.authenticated = {
          ...prevUser.authenticated,
          isStronglyAuthenticated: true,
        };
      });
    },
    [setUser]
  );

  // Replaces PROCESS_EMPLOYEE_SUBSCRIPTION_PAIRING, PROCESS_EMPLOYEE_SUBSCRIPTION_PAIRING_FULFILLED, PROCESS_EMPLOYEE_SUBSCRIPTION_PAIRING_FAILED
  const processSubscriptionPairing = useSingletonCallback(
    async (request: SubscriptionPairingRequest, validationErrors?: CommonError[]) => {
      // TODO: This should be handled within component's local state
      const errors = getErrorsFromEmployeeSubscriptionPairingScene(request, validationErrors);
      if (errors) {
        setUser(prevUser => {
          if (prevUser.authenticated) {
            prevUser.authenticated.errors = errors;
          }
        });
        return;
      }
      setUser(prevUser => {
        if (prevUser.authenticated) {
          prevUser.authenticated.errors = undefined;
        }
      });
      try {
        await callUiApi(requestEmployeeOtpForPairingPrivateMethod(), {
          body: JSON.stringify(request),
        });
      } catch (error) {
        const commonErrors = await toCommonErrors(error);
        setUser(prevUser => {
          if (prevUser.authenticated) {
            prevUser.authenticated.errors = commonErrors;
          }
        });
      }
    },
    [setUser]
  );

  // Replaces VALIDATE_EMPLOYEE_SUBSCRIPTION_PAIRING, VALIDATE_EMPLOYEE_SUBSCRIPTION_PAIRING_FULFILLED, VALIDATE_EMPLOYEE_SUBSCRIPTION_PAIRING_FAILED
  const validateSubscriptionPairing = useSingletonCallback(
    async (request: ValidateSubscriptionPairingPayload, validationErrors?: CommonError[]) => {
      // TODO: This should be handled within component's local state
      const errors = getErrorsFromOneTimePasswordComponent(request, validationErrors);
      if (errors) {
        setUser(prevUser => {
          if (prevUser.authenticated) {
            prevUser.authenticated.errors = errors;
          }
        });
        return;
      }
      setUser(prevUser => {
        if (prevUser.authenticated) {
          prevUser.authenticated.errors = undefined;
          prevUser.authenticated.saving = true;
        }
      });
      try {
        const response = await callUiApi(validateEmployeeOtpAndPairPrivateMethod(), {
          body: JSON.stringify(request),
        });
        const responseData: ValidateSubscriptionPairingResponse = await response.json();
        setUser(prevUser => {
          if (prevUser.authenticated) {
            prevUser.authenticated.errors = getErrorsFromValidateEmployeeSubscriptionResponse(responseData);
            prevUser.authenticated.saving = false;
          }
        });
        if (responseData.status === 'PAIRED') {
          dispatch(clearEmployeeSubs());
          dispatch(push(request.redirectPath));
          await validateAuth();
          dispatch(getEmployeeSubscriptions(SubscriptionCategory.VOICE));
          dispatch(getEmployeeSubscriptions(SubscriptionCategory.BROADBAND));
          dispatch(getEmployeeSubscriptions(SubscriptionCategory.DEVICE));
        }
      } catch (error) {
        const commonErrors = await toCommonErrors(error);
        setUser(prevUser => {
          if (prevUser.authenticated) {
            prevUser.authenticated.errors = filterErrors(commonErrors);
            prevUser.authenticated.saving = false;
          }
        });
        const conflictDialogParams = handleConflictedError(commonErrors, SwitchAccountJourneyPoint.ONBOARDING);
        if (conflictDialogParams) {
          openDialog(conflictDialogParams);
        }
      }
    },
    [dispatch, setUser, validateAuth]
  );

  const filterExpiringEppSubs = useSingletonCallback(
    (subscriptionIdToFilterOut: string) => {
      setUser(prevUser => {
        if (prevUser.authenticated?.expiringEppSubscriptions) {
          prevUser.authenticated.expiringEppSubscriptions = prevUser.authenticated.expiringEppSubscriptions.filter(
            subscriptionId => subscriptionId !== subscriptionIdToFilterOut
          );
        }
      });
    },
    [setUser]
  );

  const resetErrors = useSingletonCallback(() => {
    setUser(prevUser => {
      prevUser.anonymous.errors = undefined;
      if (prevUser.authenticated) {
        prevUser.authenticated.errors = undefined;
      }
    });
  }, [setUser]);

  useEffect(() => {
    subscribeAuthAction('LOGIN', login);
    subscribeAuthAction('VALIDATE', validateAuth);
    subscribeAuthAction('LOGOUT', logout);
    subscribeAuthAction('SET_ACTIVE_ACCOUNT', setActiveAccount);
    subscribeAuthAction('UPDATE_AUTH_RESULT', updateAuthResult);
    subscribeAuthAction('FILTER_EXPIRING_EPP_SUBS', filterExpiringEppSubs);
    subscribeAuthAction('RESET_ERRORS', resetErrors);
    return () => {
      unsubscribeAuthAction('LOGIN', login);
      unsubscribeAuthAction('VALIDATE', validateAuth);
      unsubscribeAuthAction('LOGOUT', logout);
      unsubscribeAuthAction('SET_ACTIVE_ACCOUNT', setActiveAccount);
      unsubscribeAuthAction('UPDATE_AUTH_RESULT', updateAuthResult);
      unsubscribeAuthAction('FILTER_EXPIRING_EPP_SUBS', filterExpiringEppSubs);
      unsubscribeAuthAction('RESET_ERRORS', resetErrors);
    };
  }, [login, validateAuth, logout, setActiveAccount, updateAuthResult, filterExpiringEppSubs, resetErrors]);

  return {
    prepareElisaIdLogin,
    processElisaIdLogin,
    logout,
    addMissingMobileNumberAndLogIn,
    selectPrimaryAccountAndLogIn,
    resolveMissingPrimaryAccount,
    validateAuth,
    login,
    verifyEmail,
    updateForgotPasswordStepNumber,
    sendOtpToUser,
    validateOtp,
    resetPassword,
    selectUsername,
    getUser,
    updateUserDetails,
    mfaRequired,
    setActiveAccount,
    switchAccount,
    identifyStrongly,
    processSubscriptionPairing,
    validateSubscriptionPairing,
  };
};

export const defaultValues: AuthServiceProps = {
  prepareElisaIdLogin: () => {},
  processElisaIdLogin: () => {},
  logout: () => {},
  addMissingMobileNumberAndLogIn: () => {},
  selectPrimaryAccountAndLogIn: () => {},
  resolveMissingPrimaryAccount: () => {},
  validateAuth: () => {},
  login: () => {},
  verifyEmail: () => {},
  updateForgotPasswordStepNumber: () => {},
  sendOtpToUser: () => {},
  validateOtp: () => {},
  resetPassword: () => {},
  selectUsername: () => {},
  getUser: () => {},
  updateUserDetails: () => {},
  mfaRequired: () => {},
  setActiveAccount: () => {},
  switchAccount: () => {},
  identifyStrongly: async () => {},
  processSubscriptionPairing: () => {},
  validateSubscriptionPairing: () => {},
};
